import React, { useState } from "react";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../UIcss/MainUI.css";
import {
  FaHome,
  FaBell,
  FaMagic,
  FaLaptopCode,
  FaPalette,
  FaHeading,
  FaIcons,
  FaKeyboard,
  FaWindowRestore,
  FaSpinner,
  FaTable,
  FaChevronDown,
  FaCrown,
  FaUsers,
  FaSearch,
  FaBolt,
} from "react-icons/fa";

// Direct import components for prerendering
import Heading from "./Heading.jsx";
import Alerts from "./Alerts.jsx";
import Animations from "./Animations.jsx";
import Colors from "./Colors.jsx";
import Button from "./Button.jsx";
import Entity from "./Entity.jsx";
import Icons from "./Icons.jsx";
import Inputs from "./Inputs.jsx";
import Modals from "./Modals.jsx";
import Navbars from "./Navbars.jsx";
import Spinners from "./Spinners.jsx";
import Tables from "./Tables.jsx";
import Title from "./Title.jsx";
import Home from "./UIHome.jsx";
import Aboutus from "./Aboutus.jsx";
import Footer from "./Footer.jsx";



const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const location = useLocation();

  const componentItems = [
    {
      path: "/alerts",
      label: "Alerts",
      desc: "Feedback & status messages",
      icon: <FaBell />,
    },
    {
      path: "/animations",
      label: "Animations",
      desc: "Smooth CSS & JS motions",
      icon: <FaMagic />,
    },
    {
      path: "/buttons",
      label: "Buttons",
      desc: "Interactive button collections",
      icon: <FaLaptopCode />,
    },
    {
      path: "/colors",
      label: "Colors",
      desc: "System color palette & tokens",
      icon: <FaPalette />,
    },
    {
      path: "/entity",
      label: "Entity",
      desc: "Data display & entity cards",
      icon: <FaLaptopCode />,
    },
    {
      path: "/headings",
      label: "Headings",
      desc: "Typography & header styles",
      icon: <FaHeading />,
    },
    {
      path: "/icons",
      label: "Icons",
      desc: "SVG & font icon libraries",
      icon: <FaIcons />,
    },
    {
      path: "/inputs",
      label: "Inputs",
      desc: "Form controls & user entry",
      icon: <FaKeyboard />,
    },
    {
      path: "/modals",
      label: "Modals",
      desc: "Overlay dialogs & popups",
      icon: <FaWindowRestore />,
    },
    // {
    //   path: "/navbars",
    //   label: "Navbars",
    //   desc: "Navigation & menu layouts",
    //   icon: <FaWindowRestore />,
    // },
    {
      path: "/spinners",
      label: "Spinners",
      desc: "Loading states & indicators",
      icon: <FaSpinner />,
    },
    {
      path: "/tables",
      label: "Tables",
      desc: "Structured data grids",
      icon: <FaTable />,
    },
    {
      path: "/title",
      label: "Title",
      desc: "Dynamic page titles & metadata",
      icon: <FaTable />,
    },
  ];

  // Check if any component route is active
  const isComponentActive = componentItems.some(
    (item) => location.pathname === item.path,
  );

  return (
    <div className="ui-top-bar-container">
      <div className="ui-navbar-brand">
        <button
          className="ui-mobile-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? "✕" : "☰"}
        </button>

        <div className="ui-brand-logo">
          <FaCrown className="ui-logo-icon" />
          <span className="ui-logo-text">CODEPULSE-R UI</span>
        </div>
      </div>

      <nav
        className={`ui-nav-links-wrapper ${isMobileMenuOpen ? "ui-mobile-open" : ""}`}
      >
        <div className="ui-nav-links">
          {/* Home Link */}
          <NavLink
            to="/home"
            className={({ isActive }) =>
              isActive ? "ui-nav-link ui-active" : "ui-nav-link"
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="ui-nav-icon">
              <FaHome />
            </span>
            Home
          </NavLink>

          {/* Components Dropdown */}
          <div
            className="ui-nav-dropdown"
            onMouseEnter={() => setIsDropdownOpen(true)}
            onMouseLeave={() => setIsDropdownOpen(false)}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div
              className={`ui-nav-link ${isDropdownOpen || isComponentActive ? "ui-active" : ""}`}
            >
              <span className="ui-nav-icon">
                <FaLaptopCode />
              </span>
              Components
              <FaChevronDown
                className={`ui-dropdown-arrow ${isDropdownOpen ? "open" : ""}`}
              />
            </div>
            {isDropdownOpen && (
              <div className="ui-dropdown-menu">
                <div className="ui-dropdown-grid">
                  {componentItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className="ui-dropdown-item"
                      onClick={() => {
                        setIsDropdownOpen(false);
                        setIsMobileMenuOpen(false);
                      }}
                    >
                      <div className="ui-dropdown-item-icon">{item.icon}</div>
                      <div className="ui-dropdown-item-content">
                        <div className="ui-dropdown-item-title">
                          {item.label}
                        </div>
                        <div className="ui-dropdown-item-desc">{item.desc}</div>
                      </div>
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* About Us Link */}
          <NavLink
            to="/about"
            className={({ isActive }) =>
              isActive ? "ui-nav-link ui-active" : "ui-nav-link"
            }
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <span className="ui-nav-icon">
              <FaUsers />
            </span>
            About Us
          </NavLink>
        </div>
      </nav>

      <div className="ui-navbar-actions">
        <button className="ui-search-btn" title="Search Components">
          <FaSearch />
        </button>
        <button className="ui-premium-btn">
          <FaBolt /> Get Pro
        </button>
      </div>
    </div>
  );
};

function MainUI() {
  return (
    <div className="ui-main-layout">
      <Navbar />
      <div className="ui-content-wrapper">
        <main className="ui-main-content">
            <Routes>
              <Route
                path="home"
              element={
                <>
                  <Helmet>
                    <title>CodePulse-R UI | Modern React UI Library</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/home" />
                    <meta
                      name="description"
                      content="Free, premium, and fully responsive React UI components. Explore buttons, modals, tables, and more with live code previews."
                    />
                    <meta
                      name="keywords"
                      content="react ui, ui components, design system, web development, css animations, responsive design"
                    />
                  </Helmet>
                  <Home />
                </>
              }
            />
            <Route
              path="about"
              element={
                <>
                  <Helmet>
                    <title>About Us | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/about" />
                    <meta
                      name="description"
                      content="Learn more about the team behind CodePulse-R UI and our mission to simplify modern web design."
                    />
                  </Helmet>
                  <Aboutus />
                </>
              }
            />
            <Route
              path="alerts"
              element={
                <>
                  <Helmet>
                    <title>Alerts & Notifications | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/alerts" />
                    <meta
                      name="description"
                      content="Responsive alert components for React. Toast, banner, and inline notifications with smooth animations."
                    />
                  </Helmet>
                  <Alerts />
                </>
              }
            />
            <Route
              path="animations"
              element={
                <>
                  <Helmet>
                    <title>CSS Animations Library | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/animations" />
                    <meta
                      name="description"
                      content="Explore 160+ premium CSS animations and motions for your web applications."
                    />
                  </Helmet>
                  <Animations />
                </>
              }
            />
            <Route
              path="buttons"
              element={
                <>
                  <Helmet>
                    <title>Premium React Buttons | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/buttons" />
                    <meta
                      name="description"
                      content="Collection of highly customizable and interactive buttons for modern web interfaces."
                    />
                  </Helmet>
                  <Button />
                </>
              }
            />
            <Route
              path="entity"
              element={
                <>
                  <Helmet>
                    <title>Entity & Data Cards | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/entity" />
                    <meta
                      name="description"
                      content="Modern data display and entity cards for dashboards and content-heavy sites."
                    />
                  </Helmet>
                  <Entity />
                </>
              }
            />
            <Route
              path="title"
              element={
                <>
                  <Helmet>
                    <title>Typography & Page Titles | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/title" />
                    <meta
                      name="description"
                      content="Dynamic page title styles and typography patterns for high-impact landing pages."
                    />
                  </Helmet>
                  <Title />
                </>
              }
            />
            <Route
              path="colors"
              element={
                <>
                  <Helmet>
                    <title>Color Palette & Tokens | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/colors" />
                    <meta
                      name="description"
                      content="A curated collection of color systems and tokens for consistent UI branding."
                    />
                  </Helmet>
                  <Colors />
                </>
              }
            />
            <Route
              path="headings"
              element={
                <>
                  <Helmet>
                    <title>Responsive Headings | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/headings" />
                    <meta
                      name="description"
                      content="Scaleable typography and heading components for all screen sizes."
                    />
                  </Helmet>
                  <Heading />
                </>
              }
            />
            <Route
              path="icons"
              element={
                <>
                  <Helmet>
                    <title>Icon Library & SVG Components | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/icons" />
                    <meta
                      name="description"
                      content="Integrated SVG icon sets optimized for performance and accessibility."
                    />
                  </Helmet>
                  <Icons />
                </>
              }
            />
            <Route
              path="inputs"
              element={
                <>
                  <Helmet>
                    <title>Form Inputs & Controls | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/inputs" />
                    <meta
                      name="description"
                      content="Fully styled form elements including text inputs, toggles, and select menus."
                    />
                  </Helmet>
                  <Inputs />
                </>
              }
            />
            <Route
              path="modals"
              element={
                <>
                  <Helmet>
                    <title>React Modals & Dialogs | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/modals" />
                    <meta
                      name="description"
                      content="Elegant overlay dialogs and popups with glassmorphism and smooth entry effects."
                    />
                  </Helmet>
                  <Modals />
                </>
              }
            />
            {/* <Route
              path="navbars"
              element={
                <>
                  <Helmet>
                    <title>Navigation & Navbars | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/navbars" />
                    <meta
                      name="description"
                      content="Collection of responsive navigation bars, menus, and layout headers for React applications."
                    />
                  </Helmet>
                  <Navbars />
                </>
              }
            /> */}
            <Route
              path="spinners"
              element={
                <>
                  <Helmet>
                    <title>Loading Spinners | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/spinners" />
                    <meta
                      name="description"
                      content="A collection of CSS-only loading indicators for improved user experience."
                    />
                  </Helmet>
                  <Spinners />
                </>
              }
            />
            <Route
              path="tables"
              element={
                <>
                  <Helmet>
                    <title>Data Grids & Tables | CodePulse-R UI</title>
                    <link rel="canonical" href="https://ui.codepulse-r.com/tables" />
                    <meta
                      name="description"
                      content="Responsive and feature-rich data tables for complex information display."
                    />
                  </Helmet>
                  <Tables />
                </>
              }
            />
            <Route path="" element={<Navigate to="home" replace />} />
          </Routes>
          <Footer />
      </main>
    </div>
  </div>
  );
}

export default MainUI;
